import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';
import { generateClassName } from '@klv/shared/utils/functions';
import * as gridStyles from '@klv/utils/grid/styles/grid.module.css';
import { useScrollDirection } from '@klv/hooks/useScrollDirection';
import Icon from '@klv/atoms/icon';
import { loginActions } from '../universal-navigation/utils';

import * as styles from './styles/subNav.module.css';

import type { ISubNav, ISubNavItem } from './types';

import { useAuthentication } from '@klv/context/authentication';
import type { IPlatformAction } from '../header/types';
import { AccountLogIn } from '@klv/atoms/account-log-in';
import { UniversalNavigationMobile } from '../universal-navigation/children/mobile';

export const SubNav = ({
  className = '',
  ctas = [],
  menuItems = [],
  path = '',
  homeLink = '/',
  universalCtas = [],
  ...props
}: ISubNav) => {
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const scrollDirection = useScrollDirection();
  const { t } = useTranslation();

  const [showFlag, setShowFlag] = useState(false);
  const [isUtilityNavVisible, setIsUtilityNavVisible] = useState(false);

  const mobileMenuRef = useRef<HTMLDialogElement>(null);
  
  useEffect(() => {
    const handleResize = () => {
      if (
        window.innerWidth >= 1024
        && mobileMenuRef?.current?.open
      ) {
        mobileMenuRef.current.close();
      }
    };

    const handleScroll = () => {
      if (window.scrollY > 80) {
        setShowFlag(true);
      } else {
        setShowFlag(false);
      }

      if (window.scrollY < 20) {
        setIsUtilityNavVisible(true);
      } else {
        setIsUtilityNavVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const { authenticationState: { loggedIn } } = useAuthentication();
  const platformActions: IPlatformAction[] = [
    {
      component: <AccountLogIn />,
      label: 'AccountLogIn',
    },
  ];

  if (loggedIn) {
    platformActions.push({
      label: `${t('navigation_platformActions_support_label')}`,
      href: `${t('navigation_platformActions_support_href')}`,
    });
  }

  const handleItemClick = (id: string) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const logoLink = menuItems?.[0]?.url || homeLink;
  const logoTitle = menuItems?.[0]?.label || t('organisms_navigation_logo_label');

  return (
    <div
      className={generateClassName([
        className,
        styles.subNav,
        scrollDirection === 'down' && styles.scrolled,
        showFlag && styles.showFlag,
        Boolean(openDropdownId) && styles.hasOpenDropdown,
      ])}
      onMouseLeave={() => setOpenDropdownId(null)}
      {...props}
    >
      <div className={generateClassName([gridStyles.grid, styles.container])}>
        <nav className={styles.nav}>
          <ul className={styles.list}>
            <a
              title={logoTitle}
              href={logoLink}
              className={styles.logoContainer}
              tabIndex={!showFlag ? -1 : undefined}
            >
              <Icon
                className={styles.flag}
                color='currentColor'
                iconName='BugMarkFilled'
                size={3}
              />
            </a>
            {menuItems.map(({ childItems, id, label, url: href }: ISubNavItem) => (
              <li
                className={generateClassName([
                  styles.listItem,
                  childItems?.length && styles.hasChildren,
                ])}
                key={id}
              >
                <UniversalMenuItem
                  aria-current={
                    (childItems?.some(({ url }) => url === path) && 'true')
                    || href === path
                    || undefined
                  }
                  className={generateClassName([
                    styles.link,
                    childItems?.length && styles.hasChildren,
                    openDropdownId === id && styles.active,
                  ])}
                  href={href}
                  onFocus={() => {
                    if (openDropdownId && openDropdownId !== id) {
                      setOpenDropdownId(null);
                    }
                  }}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    if (childItems?.length) {
                      e.stopPropagation();
                      e.preventDefault();
                      handleItemClick(id);
                    }
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if (Array.isArray(childItems) && childItems.length > 0) { 
                        e.stopPropagation();
                        e.preventDefault();
                        handleItemClick(id);
                      } else if (href && href !== '#' && href !== '') {
                        e.preventDefault();
                        window.location.href = href;
                      } 
                    }
                  }}
                  tabIndex={childItems?.length ? 0 : undefined}
                >
                  <span>{label}</span>
                </UniversalMenuItem>
                {childItems && childItems.length > 0 && (
                  <ul className={generateClassName([
                    styles.childList,
                    openDropdownId === id && styles.open,
                  ])}>
                    {childItems.map(({ id: childId, label: childLabel, url: childHref, ...rest }: ISubNavItem) => (
                      <li key={childId} className={styles.childListItem}>
                        <UniversalMenuItem
                          aria-current={childHref === path ? 'true' : undefined}
                          className={styles.childLink}
                          href={childHref}
                          onClick={() => loginActions({ childItem: label })}
                          {...rest}
                        >
                          {childLabel}
                        </UniversalMenuItem>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <nav className={styles.mobileSubNav}>
            <div className={styles.mobileSubNavHeader}>
              <a 
                href={logoLink}
                title={logoTitle}
              >
                <Icon
                  className={styles.mobileFlag}
                  color='currentColor'
                  iconName='BugMarkFilled'
                  size={3}
                />
              </a>
              <h2 className={generateClassName(['h3', 'semibold', styles.mobileSubNavTitle])} onClick={() => mobileMenuRef.current?.showModal()}>{menuItems?.[0]?.label}</h2>
            </div>
            <UniversalNavigationMobile
              className={generateClassName([!isUtilityNavVisible && scrollDirection === 'up' ? styles.peakaboo : '', scrollDirection === 'down' ? styles.scrolled : ''])}
              ctas={ctas}
              menuItems={menuItems}
              menuStyle="subnav"
              path={path}
              platformActions={platformActions}
              ref={mobileMenuRef}
              scrollDirection={scrollDirection}
            />
          </nav>
          <div className={styles.ctaListContainer}>
            <ul className={generateClassName([styles.list, styles.ctaList])}>
              {universalCtas.map(({ id, label, url, variant, ...rest }) => (
                <li key={id} className={styles.ctaListItem}>
                  <UniversalMenuItem tabIndex={!showFlag ? -1 : undefined} className={generateClassName([
                    styles.cta,
                    variant && styles[variant],
                  ])} href={url} label={label} {...rest} />
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
}; 
